import { useRef, useEffect, useCallback } from 'react';

const useEventListenerRef = (eventType, handler) => {
  const elementRef = useRef(null); // Exposes current DOM element
  const savedHandlerRef = useRef(handler); // Tracks latest handler
  const listenerRef = useRef(null); // Tracks current listener function

  // Keep the handler ref up-to-date
  useEffect(() => {
    savedHandlerRef.current = handler;
  }, [handler]);

  const setRef = useCallback(
    (node) => {
      // Remove old listener
      if (elementRef.current && listenerRef.current) {
        elementRef.current.removeEventListener(eventType, listenerRef.current);
        listenerRef.current = null;
      }

      if (node) {
        const listener = (event) => {
          if (savedHandlerRef.current) {
            savedHandlerRef.current(event);
          }
        };

        node.addEventListener(eventType, listener);

        // Store for cleanup
        listenerRef.current = listener;
        elementRef.current = node;
      } else {
        elementRef.current = null;
      }
    },
    [eventType]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (elementRef.current && listenerRef.current) {
        elementRef.current.removeEventListener(eventType, listenerRef.current);
      }
    };
  }, [eventType]);

  // Return both: callback ref and the actual DOM ref
  return [setRef, elementRef];
};

export default useEventListenerRef;
